import React from 'react';
import {
  CardHorizontalFeature,
  CardHorizontalFeatureProps,
  renditions,
  TrackingPropsInterface,
} from '@marriott/mi-ui-library';
import { ProductDetailsCard } from '../organisms/ProductDetailsCard';
import type { ProductDetailsCardProps } from '../organisms/ProductDetailsCard/ProductDetailsCard.types';
import { EnrollmentCardContainerComp } from '../organisms/EnrollmentCardContainer/EnrollmentCardContainerComp';
import type { EnrollmentCardContainerProps } from '../organisms/EnrollmentCardContainer/EnrollmentCardContainer.types';
import { buildDynamicMediaNode } from './dynamicMediaUtils';
import type { SlotContent } from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';

export type SlotTrackingContext = {
  index: number;
  trackingProperties?: TrackingPropsInterface;
};

const CARD_TYPE_MAP = {
  layeredfeaturecard: 'layeredfeaturecard',
  productdetailscard: 'productdetailscard',
  enrollmentcard: 'enrollmentcard',
  cobrandcard: 'cobrandcard',
  riottmediacard: 'riottmediacard',
} as const;

type CardTypeKey = (typeof CARD_TYPE_MAP)[keyof typeof CARD_TYPE_MAP];

type ResolveCtx = {
  slot: SlotContent;
  size: 'square' | 'tall' | 'banner';
  contentObj: Record<string, unknown>;
  baseUrl: string;
  alt: string;
  dynamicMedia: renditions;
  dynamicMediaWide: renditions;
  dynamicMediaTall: renditions;
  trackingCtx?: SlotTrackingContext;
};

function getPayloadObj(slot: SlotContent): Record<string, unknown> {
  const payload = slot?.payload;
  if (payload && typeof payload === 'object') return payload as Record<string, unknown>;
  return {};
}

function getCardTypeFromSlot(slot: SlotContent): string {
  const payloadObj = getPayloadObj(slot);
  return (payloadObj['cardType'] as string) || (payloadObj['cardtype'] as string) || '';
}

function normalizeCardType(input: string): CardTypeKey | null {
  const normalized = input
    .trim()
    .toLowerCase()
    .replace(/model$/, '');
  const key = normalized as keyof typeof CARD_TYPE_MAP;
  return CARD_TYPE_MAP[key] ?? null;
}

function buildResolveCtx(slot: SlotContent, trackingCtx?: SlotTrackingContext): ResolveCtx {
  const size = (slot?.size as 'square' | 'tall' | 'banner') ?? 'square';

  // Extract content data from slot for downstream card components.
  const contentObj = getPayloadObj(slot);
  const baseUrl = (contentObj['assetPath'] as string) || (contentObj['image'] as string) || '';
  const alt = (contentObj['imageAltText'] as string) || '';

  const EMPTY_DynamicMedia: renditions = { altText: '', assetPath: '', dynamic: false, renditions: [] };
  const dynamicMedia = buildDynamicMediaNode(baseUrl, 'square', alt) || EMPTY_DynamicMedia;
  const dynamicMediaWide = buildDynamicMediaNode(baseUrl, 'wide', alt) || EMPTY_DynamicMedia;
  const dynamicMediaTall = buildDynamicMediaNode(baseUrl, 'tall', alt) || EMPTY_DynamicMedia;

  return { slot, size, contentObj, baseUrl, alt, dynamicMedia, dynamicMediaWide, dynamicMediaTall, trackingCtx };
}

export function resolveSlotComponent(slot: SlotContent, trackingCtx?: SlotTrackingContext): React.ReactElement | null {
  const size = (slot?.size as 'square' | 'tall' | 'banner') ?? 'square';
  const resolvedCardType =
    size === 'banner'
      ? CARD_TYPE_MAP.layeredfeaturecard
      : slot?.type === 'cf'
      ? normalizeCardType(getCardTypeFromSlot(slot)) || CARD_TYPE_MAP.productdetailscard
      : // TODO: Below is for DCA slots, remove this fallback once we have a proper card type mapping
        CARD_TYPE_MAP.productdetailscard; // fallback to ProductDetailsCard for unknown card types
  if (!resolvedCardType) return null;

  const key = resolvedCardType;
  const ctx = buildResolveCtx(slot, trackingCtx);
  const CardRenderers: Record<CardTypeKey, (ctx: ResolveCtx) => React.ReactElement | null> = {
    [CARD_TYPE_MAP.layeredfeaturecard]: ctx => (
      <CardHorizontalFeature
        {...({
          ...(ctx.contentObj as Record<string, unknown>),
          header: (ctx.contentObj?.['title'] as string) || '',
          eyebrowtext: (ctx.contentObj?.['eyebrow'] as string) || '',
          dynamicMedia: ctx.dynamicMediaWide,
        } as CardHorizontalFeatureProps)}
      />
    ),
    [CARD_TYPE_MAP.productdetailscard]: ctx => {
      const idx = ctx.trackingCtx?.index ?? 0;
      const baseTracking = ctx.trackingCtx?.trackingProperties ?? ({} as TrackingPropsInterface);
      const productDetailsCardProps = {
        ...(ctx.contentObj as Record<string, unknown>),
        breakPointKey: 'viewportL',
        fileReferenceImage: ctx.baseUrl,
        dynamicMedia: ctx.dynamicMedia,
        dynamicMediaWide: ctx.dynamicMediaWide,
        dynamicMediaTall: ctx.dynamicMediaTall,
        cardVariation: ctx.size === 'banner' ? 'square' : ctx.size,
        index: idx,
        trackingProperties: {
          ...baseTracking,
          trackingDescription: `Card${idx + 1}-${baseTracking.trackingDescription || ''}`,
        },
      } as ProductDetailsCardProps;

      return <ProductDetailsCard {...productDetailsCardProps} />;
    },
    // To do: Normalization of enrollment card payload
    [CARD_TYPE_MAP.enrollmentcard]: ctx => {
      const enrollmentCardProps = {
        ...(ctx.contentObj as Record<string, unknown>),
        fileReferenceImage: ctx.baseUrl,
        dynamicMedia: ctx.dynamicMedia,
        dynamicMediaWide: ctx.dynamicMediaWide,
        dynamicMediaTall: ctx.dynamicMediaTall,
      } as unknown as EnrollmentCardContainerProps;
      return <EnrollmentCardContainerComp {...enrollmentCardProps} />;
    },
    [CARD_TYPE_MAP.cobrandcard]: _ctx => null,
    [CARD_TYPE_MAP.riottmediacard]: _ctx => null,
  };
  // Default/fallback: unsupported card types render ProductDetailsCard.
  const renderer = CardRenderers[key] ?? CardRenderers[CARD_TYPE_MAP.productdetailscard];
  return renderer(ctx);
}
